import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Truck } from "lucide-react";
import { api, fmtPrice } from "../lib/api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [active, setActive] = useState(0);
  const { addItem, setOpen } = useCart();

  useEffect(() => {
    window.scrollTo(0, 0);
    setActive(0);
    api.get(`/products/${id}`).then(({ data }) => setP(data)).catch(() => setP(false));
  }, [id]);

  if (p === false)
    return (
      <div className="pt-40 text-center min-h-screen">
        <p className="font-serif-ed text-3xl">Cart not found.</p>
        <Link to="/" className="text-gold font-label uppercase tracking-[0.2em] text-sm mt-4 inline-block">Back home</Link>
      </div>
    );
  if (!p) return <div className="pt-40 text-center min-h-screen font-label uppercase tracking-[0.3em] text-sm text-[var(--ggc-muted)]">Loading…</div>;

  const gallery = p.gallery && p.gallery.length ? p.gallery : [p.image];

  return (
    <div className="pt-[76px]" data-testid="product-detail">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <Link to="/#fleet" className="inline-flex items-center gap-2 font-label uppercase tracking-[0.2em] text-xs text-[var(--ggc-muted)] hover:text-gold transition-colors">
          <ArrowLeft size={16} /> Back to fleet
        </Link>
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-20 pb-24">
        <div className="lg:sticky lg:top-[100px] self-start">
          <motion.div
            key={active}
            initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-sm overflow-hidden border relative"
            style={{ borderColor: "var(--ggc-border)" }}
          >
            <div className="absolute inset-0 z-10 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 40%, rgba(212,175,55,0.14), transparent 60%)" }} />
            <img src={gallery[active]} alt={p.name} className="w-full aspect-[4/3] object-cover relative" />
          </motion.div>
          {gallery.length > 1 && (
            <div className="mt-4 grid grid-cols-4 gap-3" data-testid="product-thumbnails">
              {gallery.map((g, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`aspect-square rounded-sm overflow-hidden border transition-all ${active === i ? "opacity-100" : "opacity-50 hover:opacity-90"}`}
                  style={{ borderColor: active === i ? "var(--ggc-gold)" : "var(--ggc-border)" }}
                  data-testid={`thumb-${i}`}
                  aria-label={`View image ${i + 1}`}
                >
                  <img src={g} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          {p.featured && (
            <span className="bg-gold text-black font-label uppercase tracking-[0.15em] text-[10px] px-3 py-1 rounded-sm">Signature</span>
          )}
          <p className="font-label uppercase tracking-[0.3em] text-xs text-gold mt-4">{p.tagline}</p>
          <h1 className="font-serif-ed text-5xl md:text-6xl mt-2">{p.name}</h1>
          <p className="font-serif-ed text-3xl text-gold mt-4">{fmtPrice(p.price)}</p>
          <p className="font-body text-[var(--ggc-muted)] leading-relaxed mt-6 max-w-lg">{p.description}</p>

          <div className="mt-10 border-t border-b divide-y" style={{ borderColor: "var(--ggc-border)" }}>
            {Object.entries(p.specs).map(([k, v]) => (
              <div key={k} className="flex justify-between py-4" style={{ borderColor: "var(--ggc-border)" }}>
                <span className="font-label uppercase tracking-[0.2em] text-xs text-[var(--ggc-muted)]">{k}</span>
                <span className="font-body text-sm">{v}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mt-10">
            <button
              onClick={() => { addItem(p); toast.success(`${p.name} added`); }}
              className="flex-1 bg-gold text-black font-label uppercase tracking-[0.2em] text-sm py-4 rounded-sm hover:bg-[var(--ggc-gold-hover)] transition-colors"
              data-testid="detail-add-btn"
            >
              Add To Selection
            </button>
            <button
              onClick={() => { addItem(p); setOpen(true); }}
              className="flex-1 border font-label uppercase tracking-[0.2em] text-sm py-4 rounded-sm hover:border-[var(--ggc-gold)] hover:text-gold transition-colors"
              style={{ borderColor: "var(--ggc-border)" }}
              data-testid="detail-buy-btn"
            >
              Buy Now
            </button>
          </div>

          <div className="flex items-center gap-3 mt-6 text-[var(--ggc-muted)]">
            <Truck size={18} strokeWidth={1.3} className="text-gold" />
            <p className="font-body text-sm">Complimentary insured delivery to all fifty states.</p>
          </div>
          <div className="flex items-center gap-3 mt-2 text-[var(--ggc-muted)]">
            <Check size={18} strokeWidth={1.3} className="text-gold" />
            <p className="font-body text-sm">Secure checkout powered by Stripe.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
